import { areCareOrdersNettable, SizeModeEnum, type CareOrder } from '@talos/kyoko';
import type { SelectableCareOrder } from './SelectableCareOrder';

function getPossibleCareOrderSizeModes(careOrder: CareOrder) {
  const possibleSizeModes = new Set<SizeModeEnum>();

  if (!careOrder.isMultitoken) {
    possibleSizeModes.add(SizeModeEnum.Currency);
  }

  if (careOrder.isQtyInUnits) {
    possibleSizeModes.add(SizeModeEnum.Units);
  }

  return possibleSizeModes;
}

/**
 * Returns care orders that can be shown in the compatible-care-orders blotter.
 *
 * A row is compatible when it is not currently a member and is nettable. In modification mode,
 * removed original netting members are also included so users can add them back even if they are
 * no longer generally nettable. Modification mode also locks the SizeMode to the existing working
 * order, so compatible rows must support the selected mode.
 */
export function getCompatibleCareOrders({
  allCareOrders,
  isModificationMode,
  referenceCareOrder,
  sizeMode,
}: {
  allCareOrders: SelectableCareOrder[];
  isModificationMode: boolean;
  referenceCareOrder?: CareOrder;
  sizeMode: SizeModeEnum;
}) {
  return allCareOrders.filter(careOrder => {
    if (careOrder.isSelected) {
      return false;
    }

    if (isModificationMode && !getPossibleCareOrderSizeModes(careOrder).has(sizeMode)) {
      return false;
    }

    if (referenceCareOrder != null && !areCareOrdersNettable(referenceCareOrder, careOrder).nettable) {
      return false;
    }

    if (careOrder.canBeNetted.result) {
      return true;
    }

    return isModificationMode && careOrder.inNettedOrder;
  });
}
